// Type-only, so the shell does not drag the server module into the bundle.
import type { Me } from "@hub/routes/me";
import { ThemeToggle } from "@hub/ui/ThemeToggle";
import { LogOut } from "lucide-react";
import { useState } from "react";

/**
 * The Nymbl mark, drawn from the brand gradient tokens rather than an image.
 *
 * design-system/assets/ ships no logo file (see its README), so an <img> here
 * would either 404 or need a binary committed for one 32px square. The gradient
 * stops ARE the brand — they come straight from tokens.css, so a re-brand moves
 * this mark with everything else.
 */
function HelmMark() {
  return (
    <span
      aria-hidden="true"
      className="grid size-8 shrink-0 place-items-center rounded-[10px] font-display text-[15px] font-bold text-white"
      style={{ background: "var(--nymbl-gradient-45)" }}
    >
      H
    </span>
  );
}

/**
 * Chrome shared by every page: brand, nav, the signed-in operator, theme,
 * sign out.
 *
 * Audience and the signed-in address both come from `me` (`/api/me`, loaded
 * in App). `me` is null while that request is in flight, so Fleet and the
 * address stay hidden rather than flashing. Releases is offered to everyone
 * — the two destinations routeFor() itself resolves between.
 */
export function AppShell({ me, children }: { me: Me | null; children: React.ReactNode }) {
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await fetch("/api/auth/sign-out", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
      });
    } finally {
      // Go to /login either way. A sign-out that failed still leaves someone
      // looking at a console they asked to leave.
      window.location.href = "/login";
    }
  }

  const email = me?.email;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-border bg-card/85 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-[1600px] items-center gap-2 px-4 sm:gap-3 sm:px-6">
          <a href="/" className="flex shrink-0 items-center gap-2.5 no-underline">
            <HelmMark />
            <span className="hidden font-display text-[15px] font-semibold tracking-tight text-foreground sm:inline">
              Nymbl Helm
            </span>
          </a>

          {/* Scrolls inside its own strip on a narrow screen so the page never does. */}
          <nav className="ml-2 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto sm:ml-6 sm:flex-1">
            {me?.isAdmin ? (
              <>
                <a
                  href="/"
                  className="shrink-0 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground"
                >
                  Fleet
                </a>
                <a
                  href="/orgs"
                  className="shrink-0 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground"
                >
                  Organizations
                </a>
                <a
                  href="/channels"
                  className="shrink-0 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground"
                >
                  Channels
                </a>
              </>
            ) : null}
            <a
              href="/releases"
              className="shrink-0 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground"
            >
              Releases
            </a>
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-3">
            {email ? (
              <span className="hidden text-sm text-muted-foreground md:inline">{email}</span>
            ) : null}
            <ThemeToggle />
            <button
              type="button"
              onClick={signOut}
              disabled={signingOut}
              className="flex size-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-8 sm:px-6">{children}</main>

      <footer className="border-t border-border px-6 py-4">
        <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>Operations plane only.</span>
          <span>
            It reads no instance database and stores nothing about anyone's work. OAuth material
            does live here: the broker holds NYMBL's client secrets, and the operator's sign-in
            tokens are stored in the account table.
          </span>
        </div>
      </footer>
    </div>
  );
}
